import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DateTime } from 'luxon';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, tap, toArray } from 'rxjs/operators';
import { Apiservice } from '../../../../services/ApiService';
import { ExcelService } from '../../../../services/ExcelService';
import { VenueService } from '../../../../services/VenueService';
import {
  VenueContinentGroup,
  VenueEntry,
} from '../../../../models/venue-admin.model';

/** Per-result-code reject counts, keyed by code (e.g. `EVENT_RESTRICTION`). */
type RejectCounts = Record<string, number>;

/** The headline scan counts shared by venue and event rows. */
interface ScanCounts {
  entries: number;
  exits: number;
  internal: number;
  reentries: number;
  inVenue: number;
  rejects: RejectCounts;
}

/** One event's figures within a venue, summed across the selected date range. */
interface EventRow extends ScanCounts {
  /** The event code from the attendance summary's `events` map. */
  key: string;
  /** Event description; see {@link eventDescription}. */
  name: string;
  /** Event date (yyyy-MM-dd) as supplied by the server, or '' if absent. */
  date: string;
}

/**
 * One venue's figures summed across the selected date range, plus the per-event
 * breakdown the row expands into.
 */
interface HistoricalRow extends ScanCounts {
  key: string;
  name: string;
  events: EventRow[];
}

/** Sort direction for the results table headers. */
type SortDir = 'asc' | 'desc';

/** How many venue requests to keep in flight at once during an extract. */
const EXTRACT_CONCURRENCY = 5;

/**
 * "Get historical data" modal for the dashboard. Lets a Ticketmaster user pick
 * any set of venues (by continent, city, or individually) and a date range,
 * then fans out the same range attendance call the /attendance page uses and
 * presents one summed row per venue. The result is downloadable as Excel and
 * copyable as TSV.
 *
 * The component owns its own venue list (fetched lazily on first open) so it's
 * self-contained and the dashboard only toggles its visibility.
 */
@Component({
  selector: 'app-historical-data-modal',
  standalone: false,
  templateUrl: './historical-data-modal.component.html',
  styleUrls: ['./historical-data-modal.component.scss', '../../styles/shared.scss'],
})
export class HistoricalDataModalComponent implements OnChanges {
  /** Drives visibility; the dashboard flips this to open/close the modal. */
  @Input() open: boolean = false;
  @Output() closed = new EventEmitter<void>();

  /** Today's date (yyyy-MM-dd), used as the default range and the date cap. */
  readonly today: string = DateTime.now().toFormat('yyyy-MM-dd');
  startDate: string = this.today;
  endDate: string = this.today;

  /** Full venue list and its continent -> city -> venue tree for the picker. */
  venues: VenueEntry[] = [];
  tree: VenueContinentGroup[] = [];
  venuesLoading: boolean = false;
  venuesError: boolean = false;

  /** Selected venue keys; the source of truth for the checkbox tree. */
  readonly selected: Set<string> = new Set<string>();
  searchTerm: string = '';
  /** Continent/city group ids that are expanded in the picker. */
  private readonly expanded: Set<string> = new Set<string>();

  /** Extract progress. */
  extracting: boolean = false;
  extractError: string = '';
  completedCount: number = 0;
  totalCount: number = 0;
  /** Names of venues whose request failed, surfaced after an extract. */
  failedVenues: string[] = [];

  /** Results table state. */
  rows: HistoricalRow[] = [];
  sortedRows: HistoricalRow[] = [];
  rejectColumns: string[] = [];
  rejectsExpanded: boolean = false;
  sortKey: string = 'entries';
  sortDir: SortDir = 'desc';
  /** When true, venues with no scan activity over the range are hidden from the table. */
  hideEmpty: boolean = false;
  /** Set once an extract has produced rows, so the results area shows. */
  hasResults: boolean = false;
  copyState: 'idle' | 'copied' = 'idle';
  /** Venue keys whose per-event breakdown is currently expanded in the table. */
  private readonly expandedRows: Set<string> = new Set<string>();

  constructor(
    private readonly apiService: Apiservice,
    private readonly venueService: VenueService,
    private readonly excelService: ExcelService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open && this.venues.length === 0 && !this.venuesLoading) {
      this.loadVenues();
    }
  }

  /** Fetches the full venue list once, building the picker tree from it. */
  private loadVenues(): void {
    this.venuesLoading = true;
    this.venuesError = false;
    this.venueService.getVenueList().subscribe({
      next: (venues) => {
        this.venues = venues;
        this.tree = this.venueService.buildVenueTree(venues);
        this.venuesLoading = false;
      },
      error: () => {
        this.venuesError = true;
        this.venuesLoading = false;
      },
    });
  }

  close(): void {
    this.closed.emit();
  }

  // ---------------------------------------------------------------------------
  // Venue picker — continent -> city -> venue checkbox tree
  // ---------------------------------------------------------------------------

  /** Composite id used to key a city within its continent. */
  cityId(continent: string, city: string): string {
    return continent + ':' + city;
  }

  isExpanded(id: string): boolean {
    return this.expanded.has(id);
  }

  toggleGroup(id: string): void {
    if (this.expanded.has(id)) {
      this.expanded.delete(id);
    } else {
      this.expanded.add(id);
    }
  }

  isVenueSelected(key: string): boolean {
    return this.selected.has(key);
  }

  toggleVenue(key: string): void {
    if (this.selected.has(key)) {
      this.selected.delete(key);
    } else {
      this.selected.add(key);
    }
  }

  /** Venues under a city that currently pass the search filter. */
  private venuesInCity(continent: string, city: string): VenueEntry[] {
    const group = this.tree.find((c) => c.continent === continent);
    const cityGroup = group?.cities.find((cy) => cy.city === city);
    return this.filterVenues(cityGroup?.venues ?? []);
  }

  /** Every venue under a continent that currently passes the search filter. */
  private venuesInContinent(continent: string): VenueEntry[] {
    const group = this.tree.find((c) => c.continent === continent);
    if (!group) return [];
    return group.cities.flatMap((cy) => this.filterVenues(cy.venues));
  }

  isCitySelected(continent: string, city: string): boolean {
    const venues = this.venuesInCity(continent, city);
    return venues.length > 0 && venues.every((v) => this.selected.has(v.key));
  }

  isCityIndeterminate(continent: string, city: string): boolean {
    const venues = this.venuesInCity(continent, city);
    const count = venues.filter((v) => this.selected.has(v.key)).length;
    return count > 0 && count < venues.length;
  }

  toggleCity(continent: string, city: string): void {
    const venues = this.venuesInCity(continent, city);
    const selectAll = !this.isCitySelected(continent, city);
    for (const venue of venues) {
      if (selectAll) {
        this.selected.add(venue.key);
      } else {
        this.selected.delete(venue.key);
      }
    }
  }

  isContinentSelected(continent: string): boolean {
    const venues = this.venuesInContinent(continent);
    return venues.length > 0 && venues.every((v) => this.selected.has(v.key));
  }

  isContinentIndeterminate(continent: string): boolean {
    const venues = this.venuesInContinent(continent);
    const count = venues.filter((v) => this.selected.has(v.key)).length;
    return count > 0 && count < venues.length;
  }

  toggleContinent(continent: string): void {
    const venues = this.venuesInContinent(continent);
    const selectAll = !this.isContinentSelected(continent);
    for (const venue of venues) {
      if (selectAll) {
        this.selected.add(venue.key);
      } else {
        this.selected.delete(venue.key);
      }
    }
  }

  /** Selects every venue currently matching the search filter. */
  selectAllVisible(): void {
    for (const continent of this.tree) {
      for (const venue of this.venuesInContinent(continent.continent)) {
        this.selected.add(venue.key);
      }
    }
  }

  clearSelection(): void {
    this.selected.clear();
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  /**
   * Filters a city's venues by the search term. Returns the full list when no
   * term is entered; matching is case-insensitive against the venue name.
   */
  filterVenues(venues: VenueEntry[]): VenueEntry[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return venues;
    return venues.filter((venue) => venue.value.name.toLowerCase().includes(term));
  }

  continentHasMatches(group: VenueContinentGroup): boolean {
    return group.cities.some((city) => this.filterVenues(city.venues).length > 0);
  }

  cityHasMatches(venues: VenueEntry[]): boolean {
    return this.filterVenues(venues).length > 0;
  }

  trackByContinent(_index: number, group: VenueContinentGroup): string {
    return group.continent;
  }

  trackByCity(_index: number, city: { city: string }): string {
    return city.city;
  }

  trackByVenue(_index: number, venue: VenueEntry): string {
    return venue.key;
  }

  // ---------------------------------------------------------------------------
  // Extract — one range call per selected venue, with a progress bar
  // ---------------------------------------------------------------------------

  get canExtract(): boolean {
    return (
      !this.extracting &&
      this.selected.size > 0 &&
      !!this.startDate &&
      !!this.endDate &&
      this.startDate <= this.endDate
    );
  }

  /** True when both dates are set but the range is inverted. */
  get rangeInvalid(): boolean {
    return !!this.startDate && !!this.endDate && this.startDate > this.endDate;
  }

  get progressPercent(): number {
    if (this.totalCount === 0) return 0;
    return Math.round((this.completedCount / this.totalCount) * 100);
  }

  /**
   * Fans out the range attendance call across the selected venues, capping
   * concurrency so we don't flood the API, and tallies each completion to drive
   * the progress bar. A failed venue is recorded and skipped rather than
   * aborting the whole extract.
   */
  extract(): void {
    if (!this.canExtract) return;

    const selectedVenues = this.venues.filter((venue) => this.selected.has(venue.key));
    this.extracting = true;
    this.extractError = '';
    this.failedVenues = [];
    this.completedCount = 0;
    this.totalCount = selectedVenues.length;
    this.hasResults = false;

    from(selectedVenues)
      .pipe(
        mergeMap(
          (venue) => this.loadVenueRow(venue),
          EXTRACT_CONCURRENCY
        ),
        toArray()
      )
      .subscribe({
        next: (rows) => {
          this.rows = rows.filter((row): row is HistoricalRow => row !== null);
          this.refreshTable();
          this.hasResults = true;
          this.extracting = false;
          if (this.rows.length === 0) {
            this.extractError = 'No data was returned for the selected venues and dates.';
          }
        },
        error: () => {
          this.extracting = false;
          this.extractError = 'The extract failed. Please try again.';
        },
      });
  }

  /**
   * Requests one venue's range summary and maps it to a row. A failed venue
   * resolves to `null` (and is recorded) so one bad venue can't fail the batch;
   * either way the completion counter ticks so the progress bar stays honest.
   */
  private loadVenueRow(venue: VenueEntry) {
    const encodedId = encodeURIComponent(venue.key);
    return this.apiService.getAttendance(encodedId, this.startDate, this.endDate).pipe(
      map((data: any): HistoricalRow => ({
        key: venue.key,
        name: venue.value.name,
        ...this.extractCounts(data),
        events: this.extractEventRows(data),
      })),
      tap(() => this.completedCount++),
      catchError(() => {
        this.completedCount++;
        this.failedVenues.push(venue.value.name);
        return of(null);
      })
    );
  }

  /**
   * Pulls the headline counts and reject map from a venue's attendance summary.
   * Values arrive as strings on the wire, so each is coerced to a number; the
   * reject map is copied as-is for the per-code columns.
   */
  private extractCounts(summary: any): ScanCounts {
    return {
      entries: Number(summary?.entries || 0),
      exits: Number(summary?.exits || 0),
      internal: Number(summary?.internal || 0),
      reentries: Number(summary?.reentries || 0),
      inVenue: Number(summary?.inVenue || 0),
      rejects: (summary?.rejects as RejectCounts) || {},
    };
  }

  /**
   * Builds the per-event breakdown from the summary's `events` map (keyed by
   * event code), merging events that share the same date and description into a
   * single row with summed figures — a multi-performance day (e.g. matinee +
   * evening of the same show) collapses to one line. Each event code still
   * contributes to the merged row's `key` so the tooltip lists them all. Sorted
   * by date then entries so the breakdown reads chronologically.
   */
  private extractEventRows(summary: any): EventRow[] {
    const events = summary?.events as Record<string, any> | undefined;
    if (!events) return [];

    const merged = new Map<string, EventRow>();
    for (const code of Object.keys(events)) {
      const event = events[code];
      const name = this.eventDescription(event, code);
      const date = (event?.date ?? '').trim();
      const counts = this.extractCounts(event);
      const groupKey = date + '|' + name;

      const existing = merged.get(groupKey);
      if (existing) {
        existing.key += ', ' + code;
        this.addCounts(existing, counts);
      } else {
        // Clone rejects so accumulating into this row never mutates the response.
        merged.set(groupKey, { key: code, name, date, ...counts, rejects: { ...counts.rejects } });
      }
    }

    return Array.from(merged.values()).sort(
      (a, b) => a.date.localeCompare(b.date) || b.entries - a.entries
    );
  }

  /** Adds one set of scan counts (including each reject code) into an accumulator row. */
  private addCounts(target: ScanCounts, source: ScanCounts): void {
    target.entries += source.entries;
    target.exits += source.exits;
    target.internal += source.internal;
    target.reentries += source.reentries;
    target.inVenue += source.inVenue;
    for (const code of Object.keys(source.rejects)) {
      target.rejects[code] = (target.rejects[code] || 0) + Number(source.rejects[code] || 0);
    }
  }

  /**
   * Event description, preferring the server's first non-blank label: the event's
   * `master_desc`, then its `name`, falling back to the raw event code so a row is
   * never left without a label.
   */
  private eventDescription(event: any, eventKey: string): string {
    const masterDesc = (event?.master_desc ?? '').trim();
    if (masterDesc) return masterDesc;
    const name = (event?.name ?? '').trim();
    if (name) return name;
    return eventKey;
  }

  // ---------------------------------------------------------------------------
  // Results table — mirrors the dashboard's Venue Totals table
  // ---------------------------------------------------------------------------

  /** Total scan activity across the headline counts, used by the "hide empty" filter. */
  private rowActivity(row: HistoricalRow): number {
    return (
      row.entries +
      row.exits +
      row.internal +
      row.reentries +
      row.inVenue +
      this.rejectTotal(row.rejects)
    );
  }

  /** Rows to display: all results, or only those with activity when "hide empty" is on. */
  private visibleRows(): HistoricalRow[] {
    if (!this.hideEmpty) return this.rows;
    return this.rows.filter((row) => this.rowActivity(row) > 0);
  }

  /** How many venues are hidden by the current "hide empty" setting. */
  get hiddenCount(): number {
    return this.rows.length - this.visibleRows().length;
  }

  toggleHideEmpty(): void {
    this.hideEmpty = !this.hideEmpty;
    this.refreshTable();
  }

  /** Whether a venue row's per-event breakdown is currently expanded. */
  isRowExpanded(key: string): boolean {
    return this.expandedRows.has(key);
  }

  /** Expands or collapses a venue row's per-event breakdown. */
  toggleRowExpanded(key: string): void {
    if (this.expandedRows.has(key)) {
      this.expandedRows.delete(key);
    } else {
      this.expandedRows.add(key);
    }
  }

  /** Rebuilds the reject-code columns and re-sorts the rows from the current results. */
  private refreshTable(): void {
    const codes = new Set<string>();
    for (const row of this.rows) {
      for (const code of Object.keys(row.rejects)) {
        codes.add(code);
      }
    }
    this.rejectColumns = Array.from(codes).sort();
    this.sortedRows = this.sortRows(this.visibleRows());
  }

  /** The "All Venues Total" row: every count and reject code summed across rows. */
  get totalsRow(): HistoricalRow {
    const totals: HistoricalRow = {
      key: '__total',
      name: 'All Venues Total',
      entries: 0,
      exits: 0,
      internal: 0,
      reentries: 0,
      inVenue: 0,
      rejects: {},
      events: [],
    };
    for (const row of this.rows) {
      totals.entries += row.entries;
      totals.exits += row.exits;
      totals.internal += row.internal;
      totals.reentries += row.reentries;
      totals.inVenue += row.inVenue;
      for (const code of Object.keys(row.rejects)) {
        totals.rejects[code] = (totals.rejects[code] || 0) + Number(row.rejects[code] || 0);
      }
    }
    return totals;
  }

  toggleSort(key: string): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = key === 'name' ? 'asc' : 'desc';
    }
    this.sortedRows = this.sortRows(this.visibleRows());
  }

  sortIndicator(key: string): string {
    if (this.sortKey !== key) return '';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  toggleRejectsExpanded(): void {
    this.rejectsExpanded = !this.rejectsExpanded;
  }

  /** Turns a raw reject code like `EVENT_RESTRICTION` into a readable `Event Restriction`. */
  formatErrorCode(code: string): string {
    if (!code) return '';
    return code
      .toLowerCase()
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  rejectTotal(rejects: RejectCounts | undefined | null): number {
    if (!rejects) return 0;
    let total = 0;
    for (const code of Object.keys(rejects)) {
      total += Number(rejects[code] || 0);
    }
    return total;
  }

  rejectCount(rejects: RejectCounts | undefined | null, code: string): number {
    if (!rejects) return 0;
    return Number(rejects[code] || 0);
  }

  /** Rejects as a percentage of all scan activity (entries + exits + internal + reentries). */
  rejectPercent(row: { entries: number; exits: number; internal: number; reentries: number; rejects: RejectCounts }): number {
    const rejects = this.rejectTotal(row.rejects);
    if (!rejects) return 0;
    const denom = row.entries + row.exits + row.internal + row.reentries;
    if (!denom) return 0;
    return (rejects / denom) * 100;
  }

  /** Sorts a copy of the rows by the active column/direction, leaving `rows` untouched. */
  private sortRows(rows: HistoricalRow[]): HistoricalRow[] {
    const key = this.sortKey;
    const dir = this.sortDir;
    return [...rows].sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (key === 'name') {
        av = a.name;
        bv = b.name;
      } else if (key === '__rejectPct') {
        av = this.rejectPercent(a);
        bv = this.rejectPercent(b);
      } else if (key === '__rejectTotal') {
        av = this.rejectTotal(a.rejects);
        bv = this.rejectTotal(b.rejects);
      } else if (key.startsWith('__reject:')) {
        const code = key.slice('__reject:'.length);
        av = this.rejectCount(a.rejects, code);
        bv = this.rejectCount(b.rejects, code);
      } else {
        av = (a as any)[key] ?? 0;
        bv = (b as any)[key] ?? 0;
      }
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return dir === 'asc' ? cmp : -cmp;
    });
  }

  // ---------------------------------------------------------------------------
  // Export — Excel download + clipboard copy
  // ---------------------------------------------------------------------------

  /** Column headers in display order, including any per-code reject columns. */
  private exportHeaders(): string[] {
    return [
      'Venue',
      'Event',
      'Date',
      'Entries',
      'Exits',
      'Internal',
      'Reentries',
      'Inside',
      'Reject %',
      'Rejects',
      ...this.rejectColumns.map((code) => this.formatErrorCode(code)),
    ];
  }

  /**
   * Cell values for one export line, aligned to {@link exportHeaders}. The
   * `venue`/`event`/`date` labels are supplied by the caller so a venue summary
   * line leaves the Event and Date columns blank while each event line names its
   * event and date.
   */
  private exportCells(
    venue: string,
    event: string,
    date: string,
    counts: ScanCounts
  ): (string | number)[] {
    return [
      venue,
      event,
      date,
      counts.entries,
      counts.exits,
      counts.internal,
      counts.reentries,
      counts.inVenue,
      Number(this.rejectPercent(counts).toFixed(2)),
      this.rejectTotal(counts.rejects),
      ...this.rejectColumns.map((code) => this.rejectCount(counts.rejects, code)),
    ];
  }

  /**
   * Flattens the table into export lines: the totals row, then each venue
   * summary immediately followed by its per-event breakdown rows. Event rows
   * repeat the venue name so the export stays sortable/filterable on its own.
   */
  private exportLines(): { venue: string; event: string; date: string; counts: ScanCounts }[] {
    const lines: { venue: string; event: string; date: string; counts: ScanCounts }[] = [
      { venue: this.totalsRow.name, event: '', date: '', counts: this.totalsRow },
    ];
    for (const row of this.sortedRows) {
      lines.push({ venue: row.name, event: '', date: '', counts: row });
      for (const event of row.events) {
        lines.push({ venue: row.name, event: event.name, date: event.date, counts: event });
      }
    }
    return lines;
  }

  /** Total + per-venue + per-event rows as plain objects for Excel/clipboard. */
  private buildExportRows(): Record<string, string | number>[] {
    const headers = this.exportHeaders();
    return this.exportLines().map(({ venue, event, date, counts }) => {
      const cells = this.exportCells(venue, event, date, counts);
      const record: Record<string, string | number> = {};
      headers.forEach((header, index) => {
        record[header] = cells[index];
      });
      return record;
    });
  }

  downloadExcel(): void {
    if (this.rows.length === 0) return;
    const fileName = `historical_${this.startDate}_to_${this.endDate}`;
    this.excelService.exportAsExcelFile(this.buildExportRows(), fileName);
  }

  /** Copies the table as tab-separated values so it pastes cleanly into a sheet. */
  async copyToClipboard(): Promise<void> {
    if (this.rows.length === 0) return;
    const headers = this.exportHeaders();
    const lines = [headers.join('\t')];
    for (const { venue, event, date, counts } of this.exportLines()) {
      lines.push(this.exportCells(venue, event, date, counts).join('\t'));
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      this.copyState = 'copied';
    } catch (error) {
      console.error('Failed to copy historical data to clipboard:', error);
    }
  }

  /** Resets the transient "Copied" affordance back to its default label. */
  resetCopyState(): void {
    this.copyState = 'idle';
  }
}
